import toast, { ToastOptions } from 'react-hot-toast';

export const formatDate = (dateTime: string) =>
  new Date(dateTime).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export const formatDateTime = (dateTime: string) =>
  new Date(dateTime).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true, // Change to false for 24-hour format
  });

export const valToDateString = (val: unknown): string =>
  val == null
    ? '--'
    : new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: '2-digit' }).format(
        new Date(val as string)
      );

const RetryInterval = 200;
const MaxRetries = 10;
const RetryCounts: { [key: string]: number } = {};

// export function autoscrollOnHashChange(history: History) {
//   history.listen(location => {
//     if (location.hash) {
//       scrollToAnchor(location.hash.slice(1));
//     }
//   });
// }

// /** Use this within the app's router (e.g. before the <ConnectedRouter> element) to auto-scroll to the element identified by the hash in the URL. */
// export function AutoscrollToLocationHash(props: { history: History }) {
//   scrollToAnchor(props.history.location.hash.slice(1), true);

//   return null;
// }

const SCROLL_CONTAINER_ID = 'scroll-container';

export function scrollToAnchor(id: string, persistent = false) {
  const target = document.getElementById(id);

  if (target) {
    document.documentElement.scrollTop = target.getBoundingClientRect().top;

    delete RetryCounts[id];
  } else if (persistent) {
    const retryCount = RetryCounts[id] || 0;

    if (retryCount <= MaxRetries) {
      setTimeout(() => {
        scrollToAnchor(id, true);
      }, RetryInterval);
    } else {
      delete RetryCounts[id];
    }
  }
}

export function scrollToElement(elementId: string, options?: ScrollOptions) {
  if (options?.timeout != null) {
    setTimeout(() => scrollToElementImp(elementId, options), options.timeout);
  } else {
    scrollToElementImp(elementId, options);
  }
}

type ScrollOptions = {
  timeout?: number;
  noScrollPadding?: boolean;
  scrollContainerId?: string;
};

function scrollToElementImp(elementId: string, options?: ScrollOptions) {
  const scrollContainerId = options?.scrollContainerId != null ? options.scrollContainerId : SCROLL_CONTAINER_ID;
  const scrollContainer = document.getElementById(scrollContainerId)!;
  const elem = document.getElementById(elementId);
  const subNav = document.getElementById('subnav');
  let paddingTopOffset = options?.noScrollPadding ? 0 : 56;
  let subNavHeight = 0;

  if (subNav) {
    subNavHeight = getHeight(subNav);

    paddingTopOffset = paddingTopOffset + subNavHeight;
  }

  // if (sendToCenter) {
  //   const scrollHeight = getHeight(scrollContainer);
  //   const halfHeight = Math.round((scrollHeight - subNavHeight) / 2);

  //   paddingTopOffset = paddingTopOffset + halfHeight;
  // }

  if (elem) {
    if (supportsNativeSmoothScroll) {
      nativeSmoothScrollTo(elem, scrollContainer, paddingTopOffset);
    } else {
      smoothScrollTo(elem.offsetTop + paddingTopOffset, 600, scrollContainerId);
    }
  }
}

function getHeight(elem: HTMLElement): number {
  if (elem == null) {
    return 0;
  }

  const styles = getComputedStyle(elem);
  const height = elem.offsetHeight;
  const marginTop = parseInt(styles.marginTop);
  const marginBottom = parseInt(styles.marginBottom);

  return height + marginTop + marginBottom;
}

// detect support for the behavior property in ScrollOptions
const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

function nativeSmoothScrollTo(elem: HTMLElement, scrollContainer: HTMLElement, offsetConstant: number) {
  const topOffset = getOffsetToParent(elem, scrollContainer) || 0;
  const paddingOffset = offsetConstant;

  const isWindowScroll = scrollContainer === document.body || scrollContainer === document.documentElement;

  if (isWindowScroll) {
    window.scrollTo({
      top: topOffset - paddingOffset,
      behavior: 'smooth',
    });
  } else {
    scrollContainer.scrollTo({
      top: topOffset - paddingOffset,
      behavior: 'smooth',
    });
  }
}

function getOffsetToParent(childElem: HTMLElement | null, parentElem: HTMLElement | null) {
  let offset = 0;
  let current = childElem;
  let parent = childElem;

  while (current && current !== parentElem && parent !== parentElem) {
    offset += current.offsetTop;
    current = current.offsetParent as HTMLElement;

    // offsetParent can overshoot parentElem, so iterate separately
    while (parent !== current && parent !== parentElem) {
      parent = parent?.parentElement || null;
    }
  }

  // If loop ended without finding the parent, return null
  if (current !== parentElem && parent !== parentElem) {
    return null;
  } else {
    return offset;
  }
}

// polyfilled smooth scrolling for IE, Edge & Safari
export function smoothScrollTo(to: number, duration: number, containerId: string) {
  const element = document.getElementById(containerId)!;
  const isWindowScroll = element === document.body || element === document.documentElement;
  const start = isWindowScroll ? window.scrollY : element.scrollTop;
  const change = to - start;
  const startDate = new Date().getTime();

  // t = current time
  // b = start value
  // c = change in value
  // d = duration
  const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
    t /= d / 2;
    if (t < 1) {
      return (c / 2) * t * t + b;
    }
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  };

  const animateScroll = () => {
    const currentDate = new Date().getTime();
    const currentTime = currentDate - startDate;
    const newScrollPos = easeInOutQuad(currentTime, start, change, duration);

    if (isWindowScroll) {
      window.scrollTo(0, newScrollPos);
    } else {
      element.scrollTop = newScrollPos;
    }

    if (currentTime < duration) {
      requestAnimationFrame(animateScroll);
    } else {
      if (isWindowScroll) {
        window.scrollTo(0, to);
      } else {
        element.scrollTop = to;
      }
    }
  };

  animateScroll();
}

export const formatFileSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

export function getTopLevelDomain(url?: string): string {
  if (!url) return '';

  try {
    const { hostname } = new URL(url);
    let ret: string;

    // Split the hostname into parts (e.g., "www.example.co.uk" -> ["www", "example", "co", "uk"])
    const parts = hostname.split('.');

    // Keep only the last two segments for the top-level domain
    // For example: "example.co.uk" -> "co.uk"
    if (parts.length > 2) {
      ret = parts.slice(-2).join('.');
    } else {
      ret = hostname; // Return the hostname if no subdomain is present
    }

    return ret;
  } catch (e) {
    console.error('Invalid URL:', url);
    return '';
  }
}

export const openExternalLink = (url?: string) => {
  try {
    const validUrl = new URL(url!);

    window.open(validUrl.href, '_blank', 'noopener,noreferrer');
  } catch (error) {
    toast.error(`Invalid URL: ${url}`);
  }
};

export const copyValue = (val: string, successMessage: string, toastOptions?: ToastOptions | false) => {
  navigator.clipboard
    .writeText(val)
    .then(() => {
      if (toastOptions !== false) {
        toast.success(successMessage, { position: 'bottom-left', ...toastOptions });
      }
    })
    .catch((err) => console.error('Failed to copy URL:', err));
};
